/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Logger } from '@app/core';
import { SuppliersService } from '@app/suppliers/suppliers/suppliers.service';
import { Supplier } from '@app/suppliers/suppliers/suppliers.model';
import { CompaniesService } from '@app/settings/companies/companies.service';
import { ApiQuery } from '@app/core/http/api-query';
import { Company } from '@app/settings/companies/company.model';
import { Observable } from 'rxjs';
import { ContactPersonComponent } from '@app/shared/components/contact-person/contact-person.component';
import { map } from 'rxjs/operators';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { PropertyComponent } from '@app/shared/components/property/property.component';
import { SupplierCompany } from '@app/suppliers/suppliers/supplier-company.model';
import { ContactComponent } from '@app/shared/components/contact/contact.component';
import { UserRoleType } from '@app/settings/users/user-role-type.enum';
import { BankAccountsComponent } from '@app/shared/components/bank-accounts/bank-accounts.component';

const log = new Logger('suppliersFormComponent');

@Component({
  selector: 'app-suppliers-form',
  templateUrl: './suppliers-form.component.html',
  styleUrls: ['./suppliers-form.component.scss'],
})
export class SuppliersFormComponent implements OnInit {
  @Input() supplier: Supplier = null;
  form: FormGroup;
  UserRoleType = UserRoleType;

  constructor(
    public modal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private supplierService: SuppliersService,
    private companiesService: CompaniesService
  ) {}

  ngOnInit() {
    /*
     *  Действията се извършват в следната последователност
     *  1.Създава се формата
     *  2.Зареждат се компаниите
     *  3.Пачва се формата ако е нужно | функцията this.patchForm() сама разбира дали е нужно да се пачва
     *
     */
    this.createForm();
    /*
     * трябва да са създадени form control-ите с фирмите за да мога да ги patch-на
     */
    this.loadCompanies().subscribe(() => {
      this.patchForm();

      /*
       * премахва contact_persons от company, когато се ънчекне чекбокса на company-то
       */
      (this.form.get('companies') as FormArray).valueChanges.subscribe((companies) => {
        companies.forEach((company: any, index: number) => {
          const companiesFormControl = <FormArray>this.form.get('companies');
          const companiesCurrFormControl = <FormArray>companiesFormControl.controls[index];
          const contactPersonFormControl = companiesCurrFormControl.controls['contact_persons'];
          if (!company.selected) {
            while (contactPersonFormControl.length !== 0) {
              contactPersonFormControl.removeAt(0);
            }
          }
        });
      });
    });
  }

  /**
   * Save action
   */
  save(): void {
    if (this.supplier && this.supplier.id) {
      const supplier: Supplier = this.supplier.deserialize(this.formatPost());
      this.supplierService.update(supplier).subscribe((res) => this.modal.close(res));
    } else {
      const supplier: Supplier = new Supplier().deserialize(this.formatPost());
      this.supplierService.store(supplier).subscribe((res) => this.modal.close(res));
    }
  }

  /**
   * format value
   */
  formatPost(): any {
    const validCompanies: Array<SupplierCompany> = [];
    this.form.value.companies.forEach((company: any, ind: number) => {
      // премахва properties който са празни т.е  ако type и value на property-то са празни премахва целия обект.
      // Използва се преди да се прати поста, за да не праща ненужни неща
      company.properties.forEach((property: any, index: number) => {
        const emptyValues = Object.values(property).every((x) => x === '' || x === null);
        if (emptyValues) {
          company.properties.splice(index, 1);
        }
      });

      // премахва company-тата които не са selected
      // Използва се преди да се прати поста, за да не праща ненужни неща
      if (company.selected) {
        validCompanies.push(company);
      }
    });
    this.form.value.companies = validCompanies;
    return this.form.value;
  }

  log(form: any) {
    log.debug(form);
  }

  /**
   * Създава формата
   */
  private createForm(): void {
    this.form = this.formBuilder.group({
      name: [null, [Validators.required]],
      type_id: [null, [Validators.required]],
      note: [null],
      companies: new FormArray([]),
      active: [true],
      contacts: new FormArray([]),
      bank_accounts: new FormArray([]),
      address: [null],
      registration_number: [null],
      vat_number: [null],
      responsible_person: [null],
    });
  }

  private patchForm() {
    /**
     * гетва supplier-а и го пълни
     */
    if (this.supplier && this.supplier.id) {
      /*
       * Прави нов обект, който по-късно ще се пачне
       */
      let dataForPatch: any = { companies: [] };

      /*
       *  Слага по един празен обект за всяка компания, за да може да се изравнят индексите и при пача да няма проблеми
       */
      for (let i = 0; i < this.form.get('companies').value.length; i++) {
        dataForPatch.companies.push({});
      }
      /*
       * Гетва supplier-a от сървиса по ID
       * Тук се подават като параметри всички includes(relations), които са нужни
       */
      const apiQuery: ApiQuery = new ApiQuery()
        .setLimit(0)
        .addIncludes('companies', 'type', 'companies.pivot.contact_persons.contacts', 'contacts', 'bank_accounts');
      this.supplierService.show(this.supplier.id, apiQuery).subscribe((supplier) => {
        log.debug('supplier', supplier);

        /*
         * Взима всичко освен companies от api-to и го слага в обекта за пачване(dataForPatch)
         * companies се пълни по-надолу от supplier['companies']
         */
        dataForPatch = Object.assign({}, supplier, { companies: dataForPatch.companies });

        /*
         *  Обикаля се взетия от сървиса supplier и се нагласят някои неща, за да може да се patch-не
         */
        supplier['companies'].forEach((company: SupplierCompany, key: number) => {
          // /*
          //  * contact_persons и properties са масиви
          //  * Ако някой от тях не е празен то тогава на form control-а selected се слага value true
          //  * По този начин чекбокса на компанията се чеква
          //  */
          // if (company.contact_persons.length === 0 && company.properties.length === 0) {
          //   return;
          // }

          /*
           * Пази индекса, за да знае къде да се пълнят празните контроли
           */
          let controlIndex: number;
          this.form.get('companies')['controls'].forEach((control: FormGroup) => {
            if (company.id === control.controls.id.value) {
              controlIndex = this.form.get('companies')['controls'].indexOf(control);
              dataForPatch.companies[controlIndex] = company;
            }
          });

          this.form.get('companies')['controls'][controlIndex].get('selected').setValue(true);
          this.form
            .get('companies')
            ['controls'][controlIndex].setControl(
              'contact_persons',
              ContactPersonComponent.createMany(company.contact_persons)
            );
          this.form
            .get('companies')
            ['controls'][controlIndex].setControl('properties', PropertyComponent.createMany(company.properties));
        });

        if (supplier['contacts'].length > 0) {
          this.form.setControl('contacts', ContactComponent.createMany(supplier['contacts']));
        }

        if (supplier['bank_accounts'].length > 0) {
          this.form.setControl('bank_accounts', BankAccountsComponent.createMany(supplier['bank_accounts']));
        }

        this.form.patchValue(dataForPatch);
        // this.form.patchValue(supplier);
      });
    } else if (this.supplier) {
      this.form.patchValue(this.supplier);
    }
  }

  /**
   * зарежда Companies и за всеяко Company push-ва във Companies по един formgroup
   * @return Observable
   */
  private loadCompanies(): Observable<PaginatedResponse> {
    const query: ApiQuery = new ApiQuery().setLimit(0);
    return this.companiesService.index(query).pipe(
      map((response: PaginatedResponse) => {
        const companies: FormArray = <FormArray>this.form.controls.companies;
        response.data.forEach((company: Company) => {
          companies.push(
            this.formBuilder.group({
              id: company.id,
              name: company.name,
              shortName: company.shortName,
              selected: false,
              contact_persons: new FormArray([]),
              properties: new FormArray([]),
            })
          );
        });
        return response;
      })
    );
  }
}
